import classNames from 'classnames';
import { useCallback } from 'react';
import { useMenu } from 'react-instantsearch';
import { FormattedNumber } from 'react-intl';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import type { Skin } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { SkinIcon } from '@sorare/core/src/components/collections/SkinIcon';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { selectOne, useFacet } from '@sorare/core/src/contexts/graphql-search';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { MENU } from '@sorare/core/src/lib/filters';
import { useGetCardSetConfig } from '@sorare/core/src/set';

const SkinButton = styled(ButtonBase)`
  opacity: 0.3;
  &.active {
    opacity: 1;
    &:hover {
      opacity: 1;
    }
  }
  transition: opacity 0.15s ease;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.6;
  }
`;

type Props = {
  visible: boolean;
  setSlug: Nullable<string>;
};

const Filter = ({ visible, setSlug }: Props) => {
  const getCardSetConfig = useGetCardSetConfig();
  const set = getCardSetConfig(setSlug);
  const { items, refine } = useMenu({
    attribute: MENU.skin.attribute,
  });
  const [searchParams] = useSearchParams();
  // get active state from search params as there are limitations
  // from reading from menu items
  // (only skins with owned cards are included)
  const activeSkin = searchParams.get(SEARCH_PARAMS.SKIN);

  const onChange = useCallback(
    (skin: string) => {
      refine(skin.toLowerCase());
    },
    [refine]
  );

  if (!visible || set.skins.length === 0) {
    return null;
  }

  return (
    <Horizontal center className="FilterWidget visible">
      {set.skins.map(s => (
        <SkinButton
          key={s}
          className={classNames({
            active: activeSkin === s.toLowerCase() || !activeSkin,
          })}
          onClick={() => onChange(s)}
        >
          <SkinIcon skin={s as Skin} size={4} />
          <BrandM>
            <FormattedNumber
              value={items.find(i => i.value === s.toLowerCase())?.count || 0}
            />
          </BrandM>
        </SkinButton>
      ))}
    </Horizontal>
  );
};

export const widget = (setSlug: Nullable<string>): FilterWidget => ({
  key: MENU.skin.attribute,
  type: 'menu',
  attribute: MENU.skin.attribute,
  Component: props => <Filter {...props} setSlug={setSlug} />,
});

type BackendProps = {
  visible: boolean;
  setSlug: Nullable<string>;
  hideCount?: boolean;
};

const BackendFilter = ({ visible, setSlug, hideCount }: BackendProps) => {
  const getCardSetConfig = useGetCardSetConfig();
  const set = getCardSetConfig(setSlug);
  const { items, refine } = useFacet('skin');

  const activeSkins = items.filter(i => i.isRefined).map(i => i.value);

  if (!visible || set.skins.length === 0) {
    return null;
  }

  return (
    <Horizontal center className="FilterWidget visible">
      {set.skins.map(s => {
        const lower = s.toLowerCase();
        return (
          <SkinButton
            key={s}
            className={classNames({
              active: activeSkins.includes(lower) || activeSkins.length === 0,
            })}
            onClick={() => refine(selectOne(lower))}
          >
            <SkinIcon skin={s as Skin} size={4} />
            {!hideCount && (
              <BrandM>
                <FormattedNumber
                  value={items.find(i => i.value === lower)?.count || 0}
                />
              </BrandM>
            )}
          </SkinButton>
        );
      })}
    </Horizontal>
  );
};

export const backendWidget = (
  setSlug: Nullable<string>,
  { hideCount }: { hideCount?: boolean } = {}
): FilterWidget => ({
  key: MENU.skin.attribute,
  type: 'menu',
  attribute: MENU.skin.attribute,
  Component: props => (
    <BackendFilter {...props} setSlug={setSlug} hideCount={hideCount} />
  ),
});
